import React, { useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { buildPivotData, DataRow, PivotConfig, PivotNode } from './pivotConfig';

interface PivotWithTotalsProps {
  data: DataRow[];
  config: PivotConfig;
  showRowTotal?: boolean; // Показывать итог по строке (справа)
  showColTotal?: boolean; // Показывать итог по колонке (снизу)
}

export const UniversalPivotWithTotals: React.FC<PivotWithTotalsProps> = ({
  data,
  config,
  showRowTotal = true,
  showColTotal = true,
}) => {
  const { uniqueXCombinations, rootNode } = useMemo(() => {
    return buildPivotData(data, config);
  }, [data, config]);

  // Хелпер для фильтрации данных по конкретной X-комбинации
  const filterRowsByX = (rows: DataRow[], xCombo: any[]): DataRow[] => {
    return rows.filter(row =>
      config.xFields.every((field, index) => String(row[field]) === String(xCombo[index]))
    );
  };

  // Рекурсивный сборщик листовых узлов (чтобы потом посчитать итоги по колонкам)
  // Нам нужно знать, какие строки данных соответствуют каждой отрендеренной строке таблицы
  const renderRows = (node: PivotNode, currentYValues: any[] = []): { html: React.ReactNode[]; leafRows: DataRow[][] } => {
    let htmlResult: React.ReactNode[] = [];
    let leafRowsResult: DataRow[][] = [];

    const isMaxDepth = currentYValues.length === config.yFields.length;

    if (isMaxDepth) {
      const rowKey = `row-${currentYValues.map(v => String(v || 'empty')).join('::')}`;

      htmlResult.push(
        <TableRow key={rowKey}>
          {/* Заголовки строк (Y) */}
          {currentYValues.map((val, idx) => (
            <TableCell key={`y-${idx}-${String(val)}`} sx={{ fontWeight: 'bold' }}>
              {val}
            </TableCell>
          ))}

          {/* Ячейки данных по X-колонкам */}
          {uniqueXCombinations.map((xCombo, idx) => {
            const cellRows = filterRowsByX(node.rows, xCombo);
            const cellValue = config.bodyExpr(cellRows);
            return (
              <TableCell key={`${rowKey}-cell-${idx}`} align="right">
                {cellValue}
              </TableCell>
            );
          })}

          {/* Итог по СТРОКЕ (справа) */}
          {showRowTotal && (
            <TableCell key={`${rowKey}-total`} align="right" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
              {config.bodyExpr(node.rows)} {/* Передаем ВСЕ строки этого узла Y */}
            </TableCell>
          )}
        </TableRow>
      );

      return { html: htmlResult, leafRows: [node.rows] };
    } else {
      node.subNodes.forEach((subNode, key) => {
        const safeKey = key !== undefined && key !== null ? key : 'unknown';
        const child = renderRows(subNode, [...currentYValues, safeKey]);
        htmlResult = htmlResult.concat(child.html);
        leafRowsResult = leafRowsResult.concat(child.leafRows);
      });
    }

    return { html: htmlResult, leafRows: leafRowsResult };
  };

  // Вызываем рендер строк и получаем заодно массив данных по строкам
  const { html: tableRows } = useMemo(() => renderRows(rootNode), [rootNode, uniqueXCombinations]);

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {/* Заголовки для Y-полей */}
            {config.yFields.map((field, idx) => (
              <TableCell key={`th-y-field-${idx}-${field || 'empty'}`} sx={{ fontWeight: 'bold' }}>
                {String(field || '').toUpperCase()}
              </TableCell>
            ))}
            {/* Заголовки X-колонок */}
            {uniqueXCombinations.map((xCombo, idx) => {
              const comboKey = xCombo.map(v => String(v || 'empty')).join('-');
              return (
                <TableCell key={`th-x-combo-${idx}-${comboKey}`} align="right" sx={{ fontWeight: 'bold' }}>
                  {xCombo.join(' / ')}
                </TableCell>
              );
            })}
            {/* Заголовок для общего итога строки */}
            {showRowTotal && (
              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#e0e0e0' }}>
                ВСЬОГО
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        
        <TableBody>
          {tableRows}

          {/* Строка подвальных итогов (КОЛОНКИ) */}
          {showColTotal && (
            <TableRow sx={{ bgcolor: '#eee', fontWeight: 'bold' }}>
              {/* Объединяем ячейки заголовков Y под слово "Всего" */}
              <TableCell colSpan={config.yFields.length} sx={{ fontWeight: 'bold' }}>
                ЗАГАЛЬНИЙ ПІДСУМОК
              </TableCell>

              {/* Итоги по каждой вертикальной колонке */}
              {uniqueXCombinations.map((xCombo, idx) => {
                const colRows = filterRowsByX(data, xCombo); // Фильтруем ВСЕ исходные данные только по этой Х-колонке
                const colValue = config.bodyExpr(colRows);
                return (
                  <TableCell key={`col-total-${idx}`} align="right" sx={{ fontWeight: 'bold' }}>
                    {colValue}
                  </TableCell>
                );
              })}

              {/* Пересечение: Итог итогов (Grand Total) */}
              {showRowTotal && (
                <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#d5d5d5' }}>
                  {config.bodyExpr(data)} {/* Передаем вообще весь исходный массив данных */}
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
